import saveAs from "file-saver";
import config from "../config.json";
import { IPageable, Params } from "../types/table/interfaces.type";
import { createAxiosInstance } from "./ServicesHelpers";
import { useQuery, UseQueryResult } from "@tanstack/react-query";

const axiosInstance = createAxiosInstance(config.ADMIN_API_BASE_URL);

export interface TpsRouteType {
  id: string;
  type: string;
  name: string;
  isDeleted: boolean;
  effectiveDate: Date;
  percentage0: number;
  percentage1: number;
  percentage2: number;
  percentage3: number;
  tpsAbove: number;
  routingPlanId: string;
  provider1Id: string;
  provider2Id: string;
  provider3Id: string;
  operatorId: number;
  providerId: string;
  createdBy: string;
  updatedBy?: string;
}
export interface TpsHistory {
  id: string;
  snapshot: TpsRouteType & { timestamp: string };
  historyTimestamp: string;
  mainId: number;
  createdBy: string;
}

export const exportTpsRoute = async (
  params: Params<TpsRouteType> = {}
): Promise<void> => {
  try {
    const response = await axiosInstance.get("/TPSRoute/csv", {
      params,
      responseType: "blob",
    });

    const disposition = response.headers["content-disposition"];
    let filename = "TpsRoute.csv";
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (match && match[1]) {
        filename = match[1].replace(/['"]/g, "").trim();
      }
    }

    const blob = new Blob([response.data], { type: "text/csv" });
    saveAs(blob, filename);

    console.log("Export successful and file downloaded.");
  } catch (error: any) {
    console.error(
      "Error fetching TpsRoute :",
      error.response?.data?.message || error.message || error
    );
  }
};

export async function fetchTpsRoutings(
  params: Params<TpsRouteType>
): Promise<IPageable<TpsRouteType>> {
  try {
    const response = await axiosInstance.get("/TPSRoute", { params });
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while getting the users."
    );
  }
}

export async function fetchTpsRouting(
  id: TpsRouteType["id"]
): Promise<TpsRouteType> {
  try {
    const response = await axiosInstance.get(`/TPSRoute/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting routing-plan/${id}.`
    );
  }
}

export const addTpsRoutingApi = async (data: Partial<TpsRouteType>) => {
  try {
    const response = await axiosInstance.post("/TPSRoute", data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while adding the tps-routing."
    );
  }
};
export const TpsRoutingBulkApi = async (data: Partial<TpsRouteType>[]) => {
  try {
    const Data = data.map(({ id, ...rest }) => rest);
    const response = await axiosInstance.put("/TPSRoute", Data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while adding the tps-routing."
    );
  }
};

export const TpsRoutingCreateAllApi = async (data: Partial<TpsRouteType>[]) => {
  try {
    const Data = data.map(({ id, ...rest }) => rest);
    const response = await axiosInstance.post("/TPSRoute/all", Data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while adding the tps-routing."
    );
  }
};

export const editTpsRoutingApi = async (
  id: string,
  data: Partial<TpsRouteType>
) => {
  try {
    const response = await axiosInstance.put(`/TPSRoute/${id}`, data);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while updating the tps-routing."
    );
  }
};

export const getTpsRoutingById = async (id: string) => {
  try {
    const response = await axiosInstance.get(`/TPSRoute/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while fetching the tps-routing."
    );
  }
};

export const deleteTpsRoutingApi = async (ids: string[]): Promise<void> => {
  try {
    await axiosInstance.delete(`/TPSRoute`, { data: ids });
    console.log(`tps-routing with ID ${ids} deleted successfully.`);
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        "An error occurred while deleting the tps-routing."
    );
  }
};

export const useTpsRoutingQuery = (
  params: Params<TpsRouteType>
): UseQueryResult<IPageable<TpsRouteType>, Error> => {
  const effectiveParams = {
    ...params,
    sort: params.sort || "createdDate,desc",
  };
  const queryKey = ["tpsRouting", JSON.stringify(effectiveParams)];
  return useQuery({
    queryKey,
    queryFn: () => fetchTpsRoutings(effectiveParams),
  });
};
export async function fetchTpsHistory(
  id: TpsRouteType["id"],
  params: Params<TpsHistory> = {}
): Promise<IPageable<TpsRouteType>> {
  try {
    const response = await axiosInstance.get(`/instructions/history`, {
      params: { ...params, mainId: id, sort: "historyTimestamp,desc" },
    });
    const data = response.data;
    return {
      ...data,
      content: data.content.map((item: any) => ({
        ...item.snapshot,
        createdBy: item.createdBy ?? item.snapshot?.createdBy,
        updatedBy: item.updatedBy ?? item.snapshot?.updatedBy,
        historyTimestamp: item.historyTimestamp,
      })),
    };
  } catch (error: any) {
    throw new Error(
      error.response?.data?.message ||
        `An error occurred while getting the FallBack Routing history for ID ${id}.`
    );
  }
}
